import { useMemo } from 'react';
import type { GameStatistics } from '../types/game';

export type LeaderboardEntry = { playerId: string; playerName: string; score: number };

/** Accepts full RoomState or narrower per-game display props (end fields optional). */
export interface GameEndScreenRoomState {
  gameState: 'waiting' | 'playing' | 'finished';
  players: Array<{ id: string; name: string; score: number }>;
  finalLeaderboard?: LeaderboardEntry[];
  gameStatistics?: GameStatistics | null;
}

export interface GameEndScreenData {
  finalLeaderboard: LeaderboardEntry[];
  gameStatistics: GameStatistics | null;
}

/**
 * Single source of truth for end-screen leaderboard and statistics when game is finished.
 * Used by useGameState (GameDisplay) and by custom HostDisplays/PlayerDisplays so stats
 * show correctly on reconnection or when room-state arrives before game-ended.
 */
export function useGameEndScreenData(
  roomState: GameEndScreenRoomState | null
): GameEndScreenData | null {
  return useMemo(() => {
    if (!roomState || roomState.gameState !== 'finished') {
      return null;
    }
    const finalLeaderboard =
      roomState.finalLeaderboard ??
      [...(roomState.players || [])]
        .sort((a, b) => b.score - a.score)
        .map((p) => ({ playerId: p.id, playerName: p.name, score: p.score }));
    const gameStatistics = roomState.gameStatistics ?? null;
    return { finalLeaderboard, gameStatistics };
  }, [roomState]);
}
